import CloseButton from "@/design-system/SpecificButtons/CloseButton/CloseButton";
import { cn } from "@/lib/utils";
import { safeGetItem, safeSetItem } from "@/utils/safeStorage";
import {
  CSSProperties,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  TransitionEvent,
  useState,
} from "react";

/*
  The promo banner shell, in the two shells that ship: the desktop in-content
  panel and the mobile compact strip. Each hides itself outside its own
  breakpoint, so both can be rendered unconditionally side by side.

  Callers supply the content and an accent; everything structural — the
  travelling border, the dot field, the glow, the dismiss control and the exit
  transition — lives here so a fix to any of it lands once.
*/

/** 24×24 arrow, sized by its own class. */
export const ArrowRightIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-4 shrink-0"
    aria-hidden
  >
    <path d="M5 12h13" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

/*
  Every field is a bare "r,g,b" triple so the alphas below can be applied per
  surface. The palette is split rather than a single hue because the blue
  banner's sweep runs across three distinct blues.
*/
export type BannerAccent = {
  /** Main accent: the sweep's body, strip borders and the icon chip. */
  base: string;
  /** Deeper shade leading the sweep and washing the compact strip. */
  deep: string;
  /** Pale highlight at the sweep's peak. */
  peak: string;
  /** Softer shade for the dot field, the glow and the outer shadows. */
  dot: string;
  /** Panel gradient, top to bottom. Tinted towards the accent. */
  panelFrom: string;
  panelTo: string;
};

/*
  The primary blue throughout: deep blue leads the sweep, washed blue peaks it.
  Shared by the club-import and app banners.
*/
export const BLUE_BANNER_ACCENT: BannerAccent = {
  base: "0,148,255",
  deep: "5,118,199",
  peak: "112,195,255",
  dot: "0,148,255",
  panelFrom: "rgba(30,38,50,.92)",
  panelTo: "rgba(16,19,26,.96)",
};

/**
 * Numbered selling points under a banner title: a wrapping row on desktop,
 * stacked in the mobile card. Reads the accent from --banner-base.
 */
export const BannerPoints = ({
  points,
  direction,
  numberClassName,
}: {
  points: ReactNode[];
  direction: "row" | "column";
  numberClassName: string;
}) => (
  <ul
    className={cn(
      "flex",
      direction === "row"
        ? "flex-wrap gap-x-6 gap-y-2.5"
        : "flex-col gap-y-2.5",
    )}
  >
    {points.map((point, i) => (
      <li
        key={i}
        className={cn(
          "flex gap-2.5 border-l-2 border-[rgba(var(--banner-base),.4)] pl-3 font-sans text-sm font-medium text-neutral-100",
          // A wrapped line needs real leading; the number sits on the first line.
          direction === "row"
            ? "items-center leading-none"
            : "items-baseline leading-snug",
        )}
      >
        <span
          className={cn("font-oswald text-xs font-semibold", numberClassName)}
        >
          {String(i + 1).padStart(2, "0")}
        </span>
        {point}
      </li>
    ))}
  </ul>
);

/*
  The travelling border: a conic-gradient disc spins inside the overflow-hidden
  track, and the panel on top covers everything but the 2px ring. The disc must
  be square and longer than the box's diagonal (1700px for a full-width desktop
  banner) or the highlight clips at the corners.

  Toned down from the handoff: a shorter trail (250°, not 190°), no pure-white
  peak and lower alphas throughout, so the sweep reads as ambient rather than
  as an attention-grab. Speed lives in --animate-banner-border-spin (6s).
*/
const DISC_SIZE = 1700;

const DOT_FIELD_MASK = "linear-gradient(115deg, transparent 45%, #000)";

const discStyle = (accent: BannerAccent): CSSProperties => ({
  width: DISC_SIZE,
  height: DISC_SIZE,
  margin: `${-DISC_SIZE / 2}px 0 0 ${-DISC_SIZE / 2}px`,
  background: `conic-gradient(from 0deg, transparent 0deg, transparent 250deg, rgba(${accent.deep},.3) 300deg, rgba(${accent.base},.5) 342deg, rgba(${accent.peak},.7) 352deg, rgba(${accent.base},.28) 357deg, transparent 360deg)`,
});

const panelStyle = (accent: BannerAccent): CSSProperties => ({
  background: `linear-gradient(180deg, ${accent.panelFrom}, ${accent.panelTo})`,
});

const dotFieldStyle = (accent: BannerAccent): CSSProperties => ({
  backgroundImage: `radial-gradient(rgba(${accent.dot},.18) 1px, transparent 1px)`,
  backgroundSize: "14px 14px",
  maskImage: DOT_FIELD_MASK,
  WebkitMaskImage: DOT_FIELD_MASK,
});

const glowStyle = (accent: BannerAccent): CSSProperties => ({
  background: `linear-gradient(90deg, rgba(${accent.base},.16), transparent)`,
});

/* The compact strip skips the animated border entirely: one flat wash. */
const stripStyle = (accent: BannerAccent): CSSProperties => ({
  background: `linear-gradient(90deg, rgba(${accent.deep},.26) 0%, rgba(${accent.dot},.07) 46%, rgba(19,19,19,0) 78%), #131313`,
});

/*
  Tailwind cannot see through interpolation, so the class-driven colours read
  these instead of spelling the accent into the class name.
*/
const accentVars = (accent: BannerAccent) =>
  ({
    "--banner-base": accent.base,
    "--banner-dot": accent.dot,
  }) as CSSProperties;

/**
 * The banner's surfaces on their own, for a layout that is not a banner but has
 * to look like it belongs to one (the pre-launch club hero). The travelling
 * border stays behind: it belongs to the banner's proportions.
 */
export const bannerSurfaces = (accent: BannerAccent) => ({
  vars: accentVars(accent),
  panel: panelStyle(accent),
  dotField: dotFieldStyle(accent),
});

/**
 * The snooze/dismiss state machine. Dismissal is a not-now rather than a no,
 * so the banner comes back after `snoozeMs`.
 */
export const useSnoozedDismissal = (
  storageKey: string,
  snoozeMs = 7 * 24 * 60 * 60 * 1000,
) => {
  const [snoozed] = useState(() => {
    const dismissedAt = Number(safeGetItem(storageKey));
    if (!dismissedAt) return false;
    return Date.now() - dismissedAt < snoozeMs;
  });
  // Two steps so the exit transition can play before the banner unmounts.
  const [dismissing, setDismissing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const dismiss = () => {
    safeSetItem(storageKey, String(Date.now()));
    setDismissing(true);
  };

  /*
    Hover transitions on the CTA bubble up here too, so only the banner's own
    exit transition may unmount it.
  */
  const handleTransitionEnd = (event: TransitionEvent<HTMLElement>) => {
    if (event.target === event.currentTarget && dismissing) setDismissed(true);
  };

  return {
    hidden: snoozed || dismissed,
    dismissing,
    dismiss,
    handleTransitionEnd,
  };
};

type Props = {
  accent: BannerAccent;
  /**
   * "desktop" is the in-content banner (≥1025px only); "compact" is the sticky
   * one-line strip below that width. Each renders nothing outside its range.
   */
  variant?: "desktop" | "compact";
  /**
   * Makes the whole shell a link. A plain <a> rather than a react-router
   * <Link> where it crosses deploys; omit for a banner whose CTA is a button.
   */
  href?: string;
  /** Leading chip. The compact strip is tight enough to skip it. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Below the description, outside its <p>: a list or row. Desktop only. */
  footer?: ReactNode;
  /** Full-bleed row under the padded content, inside the panel. Desktop only. */
  strip?: ReactNode;
  cta: ReactNode;
  /** Omit both to make the banner permanent: no dismiss control renders. */
  dismissLabel?: string;
  onDismiss?: () => void;
  onTransitionEnd: (event: TransitionEvent<HTMLElement>) => void;
  dismissing: boolean;
  className?: string;
};

export const FancyBanner = ({
  accent,
  variant = "desktop",
  href,
  icon,
  title,
  description,
  footer,
  strip,
  cta,
  dismissLabel,
  onDismiss,
  onTransitionEnd,
  dismissing,
  className,
}: Props) => {
  /*
    When the shell is a link the dismiss control sits inside it, so dismissing
    must not navigate. Handled here rather than by each caller.
  */
  const handleDismiss = (event: MouseEvent) => {
    if (href) {
      event.preventDefault();
      event.stopPropagation();
    }
    onDismiss?.();
  };

  if (variant === "compact")
    return (
      <Shell
        href={href}
        style={{ ...accentVars(accent), ...stripStyle(accent) }}
        onTransitionEnd={onTransitionEnd}
        className={cn(
          "sticky top-0 z-20 touch-device:hidden",
          "flex items-center gap-[11px] px-4 py-[11px]",
          "border-t border-t-[rgba(var(--banner-base),.2)]",
          "border-b border-b-[rgba(var(--banner-base),.25)]",
          "transition-[opacity,transform] duration-200 ease-out",
          dismissing && "-translate-y-2 opacity-0",
          className,
        )}
      >
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-sans text-[13.5px] font-semibold leading-[1.3] text-[#fafafa]">
            {title}
          </h3>
          <p className="mt-0.5 truncate font-sans text-[11.5px] font-normal leading-[1.35] text-[#8f8f8f]">
            {description}
          </p>
        </div>

        {cta}

        {/*
          The DS CloseButton's 30px chip is too wide for this row, so it is
          shrunk to its bare 20px glyph; the pseudo-element restores a 44px tap
          target.
        */}
        {onDismiss && (
          <CloseButton
            aria-label={dismissLabel}
            onClick={handleDismiss}
            className={cn(
              "relative -ml-[3px] size-5 rounded-none border-transparent bg-transparent text-white",
              "hover:bg-transparent",
              "after:absolute after:inset-x-[-8px] after:top-1/2 after:h-11",
              "after:-translate-y-1/2 after:content-['']",
            )}
          />
        )}
      </Shell>
    );

  return (
    <Shell
      href={href}
      style={accentVars(accent)}
      onTransitionEnd={onTransitionEnd}
      className={cn(
        // The 2px padding is the border track; the spinning disc shows only there.
        "relative hidden touch-device:block overflow-hidden rounded-2xl p-0.5",
        "bg-white/7",
        "shadow-[0_0_0_1px_rgba(var(--banner-dot),.12),0_22px_50px_-26px_rgba(var(--banner-dot),.45)]",
        // Reduced motion: no travelling highlight, so the track carries the colour.
        "motion-reduce:bg-[rgba(var(--banner-dot),.28)]",
        "transition-[opacity,transform] duration-200 ease-out",
        dismissing && "translate-y-2 opacity-0",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 animate-banner-border-spin motion-reduce:hidden"
        style={discStyle(accent)}
      />

      <div
        className="relative overflow-hidden rounded-[14px]"
        style={panelStyle(accent)}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={dotFieldStyle(accent)}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-55"
          style={glowStyle(accent)}
        />

        <div className="relative flex items-center gap-[22px] px-[26px] py-[22px]">
          {icon && (
            <div className="flex size-12 shrink-0 items-center justify-center rounded-[13px] border border-[rgba(var(--banner-base),.3)] bg-[rgba(var(--banner-base),.1)]">
              {icon}
            </div>
          )}

          <div className="min-w-0 flex-1">
            <h3 className="font-sans text-[21px] font-semibold leading-[1.25] tracking-[-0.012em] text-[#fafafa]">
              {title}
            </h3>
            {description && (
              <p className="mt-[5px] font-sans text-[13.5px] font-normal leading-[1.5] text-pretty text-[#9a9a9a]">
                {description}
              </p>
            )}
            {footer}
          </div>

          {cta}

          {onDismiss && (
            <CloseButton
              aria-label={dismissLabel}
              onClick={handleDismiss}
              className="border-transparent bg-transparent text-white hover:bg-white/10"
            />
          )}
        </div>

        {strip}
      </div>
    </Shell>
  );
};

/** <a> when the whole banner navigates, <div> when its CTA does. */
const Shell = ({
  href,
  children,
  ...rest
}: HTMLAttributes<HTMLElement> & { href?: string; children: ReactNode }) =>
  href ? (
    <a href={href} {...rest}>
      {children}
    </a>
  ) : (
    <div {...rest}>{children}</div>
  );
